// The MONEY sub-resource hooks — financials, settlement, commissions. Deliberately a
// SEPARATE file from the money-free useShows.ts (mirroring the backend's separate Cntrl.Core.Modules.Artist.Money
// namespace): money is separate endpoints/DTOs, never conditional fields (the A3 rule). A
// money-less viewer never CALLS these — the UI gates each on the money:full/per_show/per_person capability, so
// the request never happens (the capability filter would 403 it server-side regardless). Settlement writes emit
// the `settlement-updated` signal on `show:{showId}` (money-gated delivery), invalidating the settlement key.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type FinancialsRead = components["schemas"]["FinancialsRead"];
type UpdateFinancialsRequest = components["schemas"]["UpdateFinancialsRequest"];
type SetMemberPayRequest = components["schemas"]["SetMemberPayRequest"];
type MemberPayRead = components["schemas"]["MemberPayRead"];
type SettlementRead = components["schemas"]["SettlementRead"];
type UpdateSettlementRequest = components["schemas"]["UpdateSettlementRequest"];
type ReopenSettlementRequest = components["schemas"]["ReopenSettlementRequest"];
type PosPullResponse = components["schemas"]["PosPullResponse"];
type PaymentInput = components["schemas"]["PaymentInput"];
type PaymentRow = components["schemas"]["PaymentRow"];
type CommissionsDto = components["schemas"]["CommissionsDto"];
type UpdateCommissionsRequest = components["schemas"]["UpdateCommissionsRequest"];
type BookingKpis = components["schemas"]["BookingKpis"];
type ShowMoneyList = components["schemas"]["ShowMoneyList"];
type ShowMoneyRow = components["schemas"]["ShowMoneyRow"];

/** The money for one show's row, keyed by `showId`. Re-exported so the rows can type their props. */
export type { ShowMoneyRow };

// ---- Financials (planned money, editor section 09) — money:full | money:per_show ------------------------

/** GET the show's planned money — `{ data, version }`; `version` is the e_ver the PUT sends as If-Match.*/
export function useFinancials(showId: string | undefined, enabled = true) {
  const { api } = useApp();
  return useBandQuery<FinancialsRead>(
    ["show", showId ?? "new", "financials"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/financials",
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: enabled && !!showId, staleTime: 15_000 },
  );
}

/** PUT edit the planned money. Sends `If-Match: "<version>"` (send `"0"` to create); 409 on mismatch.*/
export function useUpdateFinancials() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<FinancialsRead, { showId: string; version: number; body: UpdateFinancialsRequest }>(
    async (band, { showId, version, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/financials",
        { params: { path: { bandId: band, showId } }, headers: { "If-Match": `"${version}"` }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) =>
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "financials"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save financials")),
    },
  );
}

/** PUT full-replace the per-member SHOW PAY (ART-241 — the editor's Expenses section, where member pay now
 *  lives). A DEDICATED endpoint so section 09's member-pay write never null-wipes the deal the Financials
 *  section owns. MemberPay rides the financials read, so this invalidates that key. */
export function useSetMemberPay() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<MemberPayRead, { showId: string; body: SetMemberPayRequest }>(
    async (band, { showId, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/financials/member-pay",
        { params: { path: { bandId: band, showId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) =>
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "financials"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save member pay")),
    },
  );
}

// ---- Settlement — money:full | money:per_show (reopen: money:full) --------------------------------

/** The settlement lifecycle, as the server's CHECK constraint spells it (`ck_settlement_status`). */
const SETTLEMENT_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In progress",
  settled: "Settled",
};

/**
 * The human label for a settlement status. Same shape as `statusLabel` (tasks) and `releaseStatusLabel`
 * (releases), including the underscore-stripping fallback for a status the server grows before we do.
 *
 * It exists because `in_progress` was rendering RAW on two surfaces — the panel's status pill and the
 * net strip's sub-line. A pill whose `text-transform: uppercase` turns it into `IN_PROGRESS` looks
 * deliberate enough to survive review, which is exactly why a raw token leaks: nothing about it reads as
 * broken until you notice the app is speaking database.
 */
export function settlementStatusLabel(status: string | null | undefined): string {
  if (!status) return "Pending";
  return SETTLEMENT_STATUS_LABELS[status] ?? status.replace(/_/g, " ");
}

/** GET the settlement record — comps derive live from the guest list; net is server-computed.*/
export function useSettlement(showId: string | undefined, enabled = true) {
  const { api } = useApp();
  return useBandQuery<SettlementRead>(
    ["show", showId ?? "new", "settlement"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/settlement",
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: enabled && !!showId, staleTime: 10_000 },
  );
}

/** PUT edit the settlement. `If-Match: "<version>"` (`"0"` to create); 409 on mismatch, 422
 * `settlement-locked` when settled (both surface verbatim via ApiError). */
export function useUpdateSettlement() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<SettlementRead, { showId: string; version: number; body: UpdateSettlementRequest }>(
    async (band, { showId, version, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/settlement",
        { params: { path: { bandId: band, showId } }, headers: { "If-Match": `"${version}"` }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) => invalidateSettlement(qc, bandId, vars.showId),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save settlement")),
    },
  );
}

/** POST finalize → settled. Idempotency-Key-aware server-side; computes + stores net-to-artist.*/
export function useFinalizeSettlement() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<SettlementRead, string>(
    async (band, showId) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/settlement/finalize",
        { params: { path: { bandId: band, showId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, showId) => invalidateSettlement(qc, bandId, showId),
      onError: (err) => pushToast(errorMessage(err, "Couldn't finalize settlement")),
    },
  );
}

/** POST reopen settled → in_progress (money:full) — audited.*/
export function useReopenSettlement() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<SettlementRead, { showId: string; body: ReopenSettlementRequest }>(
    async (band, { showId, body }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/settlement/reopen",
        { params: { path: { bandId: band, showId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) => {
        invalidateSettlement(qc, bandId, vars.showId);
        // Reopen reverts the SHOW's lifecycle dot (settled → confirmed), not just the worksheet (ART-241/248).
        // Without refetching the show read, the panel stays read-only and the preview's footer "Reopen show"
        // button lingers off a stale "settled" status. This prefix also re-catches the settlement sub-key.
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't reopen settlement")),
    },
  );
}

/** POST a manual per-show POS merch pull (money:show_settlement — the DOS card, ART-174). Lands through the
 * fill-only attach path, so a pull is a no-op once a figure is attached. Never toasts here — the caller reads
 * `state` (attached · already-attached · not-connected · window-open · degraded · no-timezone) and decides
 * the message. On any landing/refresh it invalidates the settlement so the merch figure re-reads. */
export function useSettlementPosPull() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<PosPullResponse, string>(
    async (band, showId) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/settlement/pull-pos",
        { params: { path: { bandId: band, showId } } },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: (_res, showId) => invalidateSettlement(qc, bandId, showId) },
  );
}

/** POST append a final-payout ledger row (money:full) — append-only.*/
export function useAppendPayment() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<PaymentRow, { showId: string; body: PaymentInput }>(
    async (band, { showId, body }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/settlement/payments",
        { params: { path: { bandId: band, showId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) => invalidateSettlement(qc, bandId, vars.showId),
      onError: (err) => pushToast(errorMessage(err, "Couldn't record payment")),
    },
  );
}

// ---- Commissions (band agent/manager % defaults) — money:full -------------------------------------------

/** GET the band's agent/manager % defaults.*/
export function useCommissions(enabled = true) {
  const { api } = useApp();
  return useBandQuery<CommissionsDto>(
    ["commissions"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/commissions", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled, staleTime: 60_000 },
  );
}

/** PUT the band's agent/manager % defaults.*/
export function useUpdateCommissions() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<CommissionsDto, UpdateCommissionsRequest>(
    async (band, body) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/commissions", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "commissions"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save commissions")),
    },
  );
}

// ---- Booking KPI money strip — money:full AND money:lifetime (B2) -----------------------

/**
 * GET the Booking KPI money strip. MONEY-BEARING and LIFETIME-gated: the route requires
 * money:full AND money:lifetime, so a per_show Tour Manager (no lifetime) and a money-free Label are 403.
 * The UI gates the whole strip on `money:lifetime` (via `useCapability`) so a seat that lacks it NEVER calls
 * this endpoint — the A3 rule client-side (the server enforces it regardless). `enabled` should be the
 * `money:lifetime` capability so a per_show/none viewer never fires the request.
 */
export function useBookingKpis(enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<BookingKpis>(
    ["booking", "kpis"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/booking/kpis", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled, staleTime: 60_000 },
  );
}

// ---- The per-show money LIST behind the Booking rows — money:full | money:per_show ----------------------

/**
 * GET the money for a WINDOW of shows — the per-row guarantee, the tour header's total, and the Past tab's
 * Profit / Merch / Total profit / Tickets cluster.
 *
 * A SEPARATE query from `useShows`, deliberately: money is separate endpoints/DTOs, never a conditional
 * field on the money-free show row (the A3 rule, and `ShowSummary` is money-free by construction on the
 * server). The two are requested over the SAME window and lined up by `showId` here on the client — the
 * `useOpportunities` money precedent.
 *
 * `enabled` is the caller's money capability. A money-free seat never mounts this, so the request never
 * happens (and would 403 if it did), and their Booking page stays money-free end to end.
 */
export function useShowMoney(
  filters: { status?: string; from?: string; to?: string; tourId?: string; limit?: number },
  enabled: boolean,
) {
  const { api } = useApp();
  return useBandQuery<ShowMoneyList>(
    ["shows", "money", filters],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shows/money", {
        params: { path: { bandId }, query: filters },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled, staleTime: 15_000 },
  );
}

/** The window's rows as a `showId → money` map, so a row looks its own figures up in O(1). */
export function showMoneyById(list: ShowMoneyList | undefined): Map<string, ShowMoneyRow> {
  const map = new Map<string, ShowMoneyRow>();
  for (const row of list?.items ?? []) if (row.showId) map.set(row.showId, row);
  return map;
}

/** Invalidate the settlement key (+ my-pay, which shares the show) after any settlement mutation. */
function invalidateSettlement(qc: ReturnType<typeof useQueryClient>, bandId: string, showId: string) {
  void qc.invalidateQueries({ queryKey: ["band", bandId, "show", showId, "settlement"] });
  void qc.invalidateQueries({ queryKey: ["band", bandId, "show", showId, "my-pay"] });
  // The Booking list's money rides its OWN query (`/shows/money`, keyed by the window), and every figure on
  // it — deposit, guarantee, tickets, row P&L, settled — is settlement-derived. Without this the row that
  // just marked a deposit received goes on rendering `Unpaid` off a cache nothing told about the write, and
  // the action reads as broken. Prefix match, so it catches whichever windows are mounted.
  void qc.invalidateQueries({ queryKey: ["band", bandId, "shows", "money"] });
}

// ── Off/travel day PAY (ART-240) — money:full | money:per_show ──────────────────────────────────────────
// The per-member day pay + the per-diem rate the day pulls from the show it attributes to. Money-gated, so it
// lives here beside the other money sub-resources, out of the money-free useShows.ts.
type TourDayPayRead = components["schemas"]["TourDayPayRead"];
type UpdateTourDayPayRequest = components["schemas"]["UpdateTourDayPayRequest"];

/** The day's per-member pay + pulled per-diem rate. Enabled only for a money seat with a real day id. */
export function useTourDayPay(tourDayId: string | undefined, enabled = true) {
  const { api } = useApp();
  return useBandQuery<TourDayPayRead>(
    ["tour-day", tourDayId ?? "new", "pay"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/tour-days/{tourDayId}/pay",
        { params: { path: { bandId, tourDayId: tourDayId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: enabled && !!tourDayId, staleTime: 15_000 },
  );
}

/** Full-replace the day's per-member pay. Invalidates the day's pay key + the tour-days list.*/
export function useUpdateTourDayPay() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<TourDayPayRead, { tourDayId: string; body: UpdateTourDayPayRequest }>(
    async (band, { tourDayId, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/tour-days/{tourDayId}/pay",
        { params: { path: { bandId: band, tourDayId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_d, vars) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "tour-day", vars.tourDayId, "pay"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "tour-days"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save day pay")),
    },
  );
}

// ── Linked days on a show (ART-240 Show Total) — money:full | money:per_show ────────────────────────────
type ShowLinkedDays = components["schemas"]["ShowLinkedDays"];

/** A show's attributed off/travel days + their cost, for the show view's "Linked days" card. Money-gated. */
export function useShowLinkedDays(showId: string | undefined, enabled = true) {
  const { api } = useApp();
  return useBandQuery<ShowLinkedDays>(
    ["show", showId ?? "new", "linked-days"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/linked-days",
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: enabled && !!showId, staleTime: 15_000 },
  );
}

// ── Show Total breakdown (booking row "Total ▾") — money:full | money:per_show ──────────────────────────
type ShowTotalBreakdown = components["schemas"]["ShowTotalBreakdown"];

/** A show's "Show Total" breakdown (income − grouped expenses = the row's Total). Fetched LAZILY — pass
 *  `enabled` only when the row's popover is open, so a page of rows costs no breakdown queries. Money-gated. */
export function useShowTotalBreakdown(showId: string | undefined, enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<ShowTotalBreakdown>(
    ["show", showId ?? "new", "total-breakdown"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/total-breakdown",
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: enabled && !!showId, staleTime: 15_000 },
  );
}

// ── Outstanding Payments (card + settle box) — money:full | money:per_show (ART-241) ────────────────────
type OutstandingList = components["schemas"]["OutstandingList"];
type OutstandingSettleBox = components["schemas"]["OutstandingSettleBox"];
export type OutstandingRow = components["schemas"]["OutstandingRow"];
export type OutstandingCollectItem = components["schemas"]["OutstandingCollectItem"];
export type OutstandingMemberLine = components["schemas"]["OutstandingMemberLine"];
export type OutstandingExpenseItem = components["schemas"]["OutstandingExpenseItem"];

/** The Outstanding Payments card — every Show/Run/Tour with anything left to settle. Money-gated. */
export function useOutstanding(enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<OutstandingList>(
    ["outstanding"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/outstanding-payments", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled, staleTime: 15_000 },
  );
}

/** The settled-history list — every DONE scope (fully settled or closed out), so it can be reopened. Fetched
 *  lazily (only while the history modal is open). */
export function useOutstandingHistory(enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<OutstandingList>(
    ["outstanding", "history"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/outstanding-payments/history",
        { params: { path: { bandId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled, staleTime: 15_000 },
  );
}

/** One scope's settle box — fetched LAZILY (only while the modal is open). Its net always counts merch
 *  (ART-290 — the settlement panel's net to artist, summed over the scope). */
export function useSettleBox(scopeKind: string | undefined, scopeId: string | undefined, enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<OutstandingSettleBox>(
    ["outstanding", scopeKind ?? "?", scopeId ?? "?"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/outstanding-payments/{scopeKind}/{scopeId}",
        { params: { path: { bandId, scopeKind: scopeKind!, scopeId: scopeId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: enabled && !!scopeKind && !!scopeId, staleTime: 5_000 },
  );
}

/** After any settle-box write, re-read the card AND the open box so both reflect the change. */
function invalidateOutstanding(qc: ReturnType<typeof useQueryClient>, bandId: string) {
  void qc.invalidateQueries({ queryKey: ["band", bandId, "outstanding"] });
}

/** Mark a COLLECT item (deposit/balance) received or not — the one-source-of-truth deposit field, so it also
 *  refreshes the show's settlement + money list. */
export function useMarkCollect() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { showId: string; kind: string; received: boolean }>(
    async (band, { showId, kind, received }) => {
      const { error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/outstanding-payments/collect/{showId}/{kind}",
        { params: { path: { bandId: band, showId, kind } }, body: { received } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: (_res, vars) => {
        invalidateOutstanding(qc, bandId);
        invalidateSettlement(qc, bandId, vars.showId);
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't update that item")),
    },
  );
}

/** Mark an EXPENSES line (a show's cost bucket or a custom line) settled, or un-settle it. */
export function useMarkExpense() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { showId: string; expenseKey: string; paid: boolean }>(
    async (band, { showId, expenseKey, paid }) => {
      const { error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/outstanding-payments/expenses/{showId}",
        { params: { path: { bandId: band, showId } }, body: { expenseKey, paid } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: (_res, vars) => {
        invalidateOutstanding(qc, bandId);
        invalidateSettlement(qc, bandId, vars.showId);
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't update that expense")),
    },
  );
}

/** Mark a member's pay line paid with a method (venmo|cash|other). */
export function useMarkMemberPaid() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { scopeKind: string; scopeId: string; personId: string; method: string }>(
    async (band, { scopeKind, scopeId, personId, method }) => {
      const { error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/outstanding-payments/{scopeKind}/{scopeId}/members/{personId}/paid",
        { params: { path: { bandId: band, scopeKind, scopeId, personId } }, body: { method } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => invalidateOutstanding(qc, bandId),
      onError: (err) => pushToast(errorMessage(err, "Couldn't mark that paid")),
    },
  );
}

/** Undo a member's paid marker (the Venmo pop-up's "Undo", or the row's un-mark). */
export function useUnmarkMemberPaid() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { scopeKind: string; scopeId: string; personId: string }>(
    async (band, { scopeKind, scopeId, personId }) => {
      const { error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/outstanding-payments/{scopeKind}/{scopeId}/members/{personId}/paid",
        { params: { path: { bandId: band, scopeKind, scopeId, personId } } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => invalidateOutstanding(qc, bandId),
      onError: (err) => pushToast(errorMessage(err, "Couldn't undo that")),
    },
  );
}

/** Close out a scope (409 if anything is still unsettled). */
export function useCloseOut() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { scopeKind: string; scopeId: string }>(
    async (band, { scopeKind, scopeId }) => {
      const { error, response } = await api.POST(
        "/api/v1/bands/{bandId}/outstanding-payments/{scopeKind}/{scopeId}/close",
        { params: { path: { bandId: band, scopeKind, scopeId } } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => invalidateOutstanding(qc, bandId),
      onError: (err) => pushToast(errorMessage(err, "Couldn't close this out")),
    },
  );
}

/** Reopen a closed-out scope (removes the close-out marker) so it becomes editable again. */
export function useReopenScope() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { scopeKind: string; scopeId: string }>(
    async (band, { scopeKind, scopeId }) => {
      const { error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/outstanding-payments/{scopeKind}/{scopeId}/close",
        { params: { path: { bandId: band, scopeKind, scopeId } } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => invalidateOutstanding(qc, bandId),
      onError: (err) => pushToast(errorMessage(err, "Couldn't reopen this")),
    },
  );
}

type VenmoQrView = components["schemas"]["VenmoQrView"];

/** A QR of the desktop "Pay with Venmo" web link — minted server-side (no client QR library). Fetched lazily
 *  (only while the pay pop-up is open on desktop). */
export function useVenmoQr(link: string | undefined, enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<VenmoQrView>(
    ["venmo-qr", link ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/outstanding-payments/venmo-qr",
        { params: { path: { bandId }, query: { link: link! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: enabled && !!link, staleTime: 60_000 },
  );
}
